import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { CrewStatsOnboardRow } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function sortOnboardRows(rows: CrewStatsOnboardRow[], groupByVessel: boolean): CrewStatsOnboardRow[] {
  return [...rows].sort((a, b) => {
    if (groupByVessel) {
      const byVessel = (a.vessel || '').localeCompare(b.vessel || '', undefined, {
        sensitivity: 'base',
      });
      if (byVessel !== 0) return byVessel;
    }
    const ao = a.rankSortOrder ?? 9999;
    const bo = b.rankSortOrder ?? 9999;
    if (ao !== bo) return ao - bo;
    return a.name.localeCompare(b.name);
  });
}

function TableHead({ showVessel }: { showVessel: boolean }) {
  return (
    <table className="crew-list-print-table so-flow-table so-flow-table-head">
      <thead>
        <tr>
          <th className="crew-list-print-col-no">No.</th>
          <th className="so-flow-col-name">Name</th>
          {showVessel ? <th className="so-flow-col-vessel">Vessel</th> : null}
          <th className="so-flow-col-rank">Rank</th>
          <th className="so-flow-col-nat">Nationality</th>
          <th className="so-flow-col-date">Sign-on</th>
          <th className="crew-list-print-col-num">Months</th>
        </tr>
      </thead>
    </table>
  );
}

function VesselGroupHeader({ label, colSpan }: { label: string; colSpan: number }) {
  return (
    <table className="crew-list-print-table so-flow-table so-flow-group-table">
      <tbody>
        <tr className="crew-list-print-group-row">
          <td colSpan={colSpan}>{label}</td>
        </tr>
      </tbody>
    </table>
  );
}

function LineRow({
  row,
  index,
  showVessel,
}: {
  row: CrewStatsOnboardRow;
  index: number;
  showVessel: boolean;
}) {
  return (
    <table className="crew-list-print-table so-flow-table so-flow-row-table">
      <tbody>
        <tr>
          <td className="crew-list-print-col-no">{index + 1}</td>
          <td className="so-flow-col-name">{row.name}</td>
          {showVessel ? <td className="so-flow-col-vessel">{dash(row.vessel)}</td> : null}
          <td className="so-flow-col-rank">{dash(row.rank)}</td>
          <td className="so-flow-col-nat">{dash(row.nationality)}</td>
          <td className="so-flow-col-date">{fmtDate(row.signOnDate)}</td>
          <td className="crew-list-print-col-num">
            {row.monthsOnboard != null ? Math.round(row.monthsOnboard) : '—'}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export type StatsOnboardReportProps = {
  rows: CrewStatsOnboardRow[];
  vesselLabel: string;
  /** When true (all vessels), insert vessel section headers in the list. */
  groupByVessel?: boolean;
};

export const StatsOnboardReport: React.FC<StatsOnboardReportProps> = ({
  rows,
  vesselLabel,
  groupByVessel = false,
}) => {
  const sortedRows = useMemo(() => sortOnboardRows(rows, groupByVessel), [groupByVessel, rows]);
  const showVessel = !groupByVessel;
  const colSpan = showVessel ? 7 : 6;

  const firstHeader = useMemo(
    () => (
      <div className="so-flow-header">
        <ReportHeader>
          <ReportTitleRow title="Onboard crew" formId="Operations" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselLabel} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
              <ReportField
                label="Sorted by"
                value={groupByVessel ? 'Vessel, then rank' : 'Rank order'}
              />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>
        <TableHead showVessel={showVessel} />
      </div>
    ),
    [groupByVessel, showVessel, vesselLabel],
  );

  const continueHeader = useMemo(
    () => (
      <div className="so-flow-header">
        <div className="so-flow-continue-bar">
          <span className="so-flow-continue-title">Onboard crew</span>
          <span className="so-flow-continue-meta">{vesselLabel}</span>
          <span className="so-flow-continue-hint">continued</span>
        </div>
        <TableHead showVessel={showVessel} />
      </div>
    ),
    [showVessel, vesselLabel],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>
          {groupByVessel
            ? 'Currently onboard crew (active ONB assignments), grouped by vessel and ordered by rank.'
            : 'Currently onboard crew (active ONB assignments), ordered by rank.'}
        </ReportNote>
      </ReportFooter>
    ),
    [groupByVessel],
  );

  const { items, itemKeys } = useMemo(() => {
    if (sortedRows.length === 0) {
      return {
        items: [
          <table key="empty" className="crew-list-print-table so-flow-table">
            <tbody>
              <tr>
                <td colSpan={colSpan} className="crew-list-print-empty">
                  No onboard crew for these filters.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }

    if (!groupByVessel) {
      return {
        items: sortedRows.map((row, i) => (
          <LineRow key={row.crewId} row={row} index={i} showVessel={showVessel} />
        )),
        itemKeys: sortedRows.map((row) => String(row.crewId)),
      };
    }

    const items: React.ReactNode[] = [];
    const itemKeys: Array<string | number> = [];
    let lastVessel: string | null = null;
    let indexInGroup = 0;

    for (const row of sortedRows) {
      const vesselKey = row.vessel?.trim() || 'Unassigned';
      if (vesselKey !== lastVessel) {
        lastVessel = vesselKey;
        indexInGroup = 0;
        items.push(
          <VesselGroupHeader key={`g-${vesselKey}`} label={vesselKey} colSpan={colSpan} />,
        );
        itemKeys.push(`g-${vesselKey}`);
      }
      items.push(
        <LineRow key={row.crewId} row={row} index={indexInGroup} showVessel={showVessel} />,
      );
      itemKeys.push(row.crewId);
      indexInGroup += 1;
    }

    return { items, itemKeys };
  }, [colSpan, groupByVessel, showVessel, sortedRows]);

  return (
    <ReportFlowPages
      className="crew-list-print-report crew-list-print-stats-onboard"
      size="portrait"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default StatsOnboardReport;
